import {SvgView} from "./SvgView";
import {createHtmlElement, createSvgElement, toggleHidden} from "../lib/domFunctions";
import {ContainerState, ContainerStateValues, ContainerType, ContainerTypeValues} from "../dto/com.rico.sb2.entity.detail";
import {ContainerDataAdapter} from "../dashboard/ContainerDataSupplier";
import {AppConfig} from "../AppConfig";
import {coalesce} from "../lib/coalesce";
import {getCurrentTime} from "../dto/com.rico.sb2.service.timeprovider";
import {ContainerIconCache} from "./ContainerIconCache";

export interface ContainerViewState {
	data: ContainerDataAdapter

	get percent(): number | null
}

interface ProgressCalculator {
	calculate(currentTime: number, data: ContainerDataAdapter): number | null
}

class ProgressNormal implements ProgressCalculator {
	calculate(currentTime: number, data: ContainerDataAdapter) {
		if (data.programFinishTime == null || data.programStartTime == null) return null;

		const period = (data.programFinishTime - data.programStartTime);
		if (period <= 0) return null;

		if (currentTime < data.programStartTime) return null

		return 100 * (currentTime - data.programStartTime) / period;
	}
}

export class ProgressWithIntermediatePoints implements ProgressCalculator {
	private readonly progressNormal = new ProgressNormal();

	calculate(currentTime: number, data: ContainerDataAdapter) {
		if (!data.programByProcess)
			return this.progressNormal.calculate(currentTime, data)

		if (data.programFinishTime == null || data.programStartTime == null) return null;

		const period = (data.programFinishTime - data.programStartTime);
		if (period <= 0) return null;

		if (currentTime < data.programStartTime) return null

		const stepDone = data.stepDonePair
		if (stepDone == null || stepDone.total == 0) return null

		let passedStepPercent = 0;

		// Если он null - значит шаг уже начался и прошел. в этом случае надо вычесть 1 из номера текущего шага
		const stepDonePercent = data.stepDonePercent(currentTime);
		if (stepDonePercent == null) {
			passedStepPercent = coalesce(stepDone.current, 0) / stepDone.total;
		} else {
			passedStepPercent = stepDone.current != null ? stepDone.current / stepDone.total : 0;
			passedStepPercent += coalesce(stepDonePercent, 0) / stepDone.total;
		}

		return 100 * passedStepPercent;
	}
}

const containerIconCache = new ContainerIconCache();

export class ContainerView extends SvgView implements ContainerViewState {
	public static PercentCalculator: ProgressCalculator = new ProgressNormal();

	static withIntermediatePoints() {
		ContainerView.PercentCalculator = new ProgressWithIntermediatePoints();
	}

	data: ContainerDataAdapter

	private percentBoundValue: string | null = null
	public onPosition: number | null = null
	public onTransport: number | null = null

	private iconBoundType: string | null = null
	private iconBoundSvg: SVGElement | null = null

	constructor(data: ContainerDataAdapter, view: SVGElement | null) {
		super(view)
		this.data = data

		this.view?.setAttribute('id', `con${data.id}`)
		this.update()
	}

	get percent() {
		return ContainerView.PercentCalculator.calculate(getCurrentTime(), this.data);
	}

	get hidden() {
		return this.data.state === null || this.data.state === ContainerState.PLANNED;
	}

	update() {
		ContainerStateValues.forEach(enumValue => this.view?.classList.remove(enumValue));
		if (this.data.state != null) this.view?.classList.add(this.data.state)
		this.updateOverexposure()

		// set type class
		ContainerTypeValues.forEach(enumValue => this.view?.classList.remove(enumValue));
		if (this.data.type != null) this.view?.classList.add(this.data.type)

		// set type icon
		if (this.iconBoundType != this.data.type) {
			this.iconBoundSvg?.remove();
			this.iconBoundSvg = null;

			this.iconBoundType = this.data.type;
			const iconBoundSvg = containerIconCache.getIcon(this.data.type)
			if (iconBoundSvg) {
				this.iconBoundSvg = iconBoundSvg
				this.bindWith('icon', svg => svg.append(iconBoundSvg))
			}
		}

		this.bind('number', this.data.number);

		toggleHidden(this.view, this.hidden || (this.data.position == null && this.data.transport == null));

		this.updateText()
	}

	private updateText() {
		const percent = this.percent;
		const percentBoundValue = percent == null ? '' : (Math.round(Math.min(percent, 100)).toString());
		if (this.percentBoundValue == percentBoundValue) return

		this.percentBoundValue = percentBoundValue
		this.bind('percent', percentBoundValue == '' ? '' : (percentBoundValue + '%'));
		this.bind('percentNumber', percentBoundValue == '' ? '0' : percentBoundValue);
	}

	private updateOverexposure() {
		this.view?.classList.toggle('OVERFLOW', this.data.overexposure)
	}

	tick() {
		this.updateOverexposure();

		if (this.data.programStartTime != null) {
			this.updateText();
		}
	}
}
